import { MousePointerClick } from '@tamagui/lucide-icons-2'
import { useCallback, useEffect, useState } from 'react'
import { View } from 'tamagui'
import { useAiHelp } from './AiHelpProvider'

interface HighlightRect {
    helpId: string
    top: number
    left: number
    width: number
    height: number
}

// Inject pulse keyframes once
if (typeof document !== 'undefined') {
    const styleId = 'ai-help-pulse-style'
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style')
        style.id = styleId
        style.textContent = `
            @keyframes ai-help-pulse {
                0%, 100% { transform: scale(1); opacity: 1; }
                50% { transform: scale(1.3); opacity: 0.7; }
            }
        `
        document.head.appendChild(style)
    }
}

export default function AiHelpHighlight() {
    const { highlights, advanceStep, isInStepFlow } = useAiHelp()
    const [rects, setRects] = useState<HighlightRect[]>([])

    const calculateRects = useCallback(() => {
        if (!highlights.length) {
            setRects([])
            return
        }

        const newRects: HighlightRect[] = []
        for (const h of highlights) {
            const el = document.querySelector(`[data-help-id="${h.helpId}"]`)
            if (el) {
                const rect = el.getBoundingClientRect()
                // Scroll into view if off-screen
                if (rect.top < 0 || rect.bottom > window.innerHeight) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }
                // Use viewport-relative coords (overlay is position: fixed)
                newRects.push({
                    helpId: h.helpId,
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                })
            }
        }
        setRects(newRects)
    }, [highlights])

    useEffect(() => {
        calculateRects()
        const timeout = setTimeout(calculateRects, 500)

        window.addEventListener('resize', calculateRects)
        window.addEventListener('scroll', calculateRects, true)

        return () => {
            clearTimeout(timeout)
            window.removeEventListener('resize', calculateRects)
            window.removeEventListener('scroll', calculateRects, true)
        }
    }, [calculateRects])

    if (!highlights.length || !rects.length) return null

    const handleCutoutClick = (helpId: string) => {
        const el = document.querySelector(`[data-help-id="${helpId}"]`) as HTMLElement | null
        if (el) {
            el.click()
            if (isInStepFlow) {
                setTimeout(() => advanceStep(), 300)
            }
        }
    }

    return (
        <View
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={1400}
            // @ts-ignore - web style
            style={{ pointerEvents: isInStepFlow ? 'auto' : 'none' }}
        >
            {/* Full-screen click blocker — only active during step flow */}
            {isInStepFlow && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'not-allowed',
                    }}
                    onClick={e => { e.stopPropagation(); e.preventDefault() }}
                />
            )}

            {/* Semi-transparent overlay with cutouts */}
            <svg
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                }}
            >
                <defs>
                    <mask id="ai-help-spotlight-mask">
                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                        {rects.map(r => (
                            <rect
                                key={r.helpId}
                                x={r.left - 4}
                                y={r.top - 4}
                                width={r.width + 8}
                                height={r.height + 8}
                                rx="8"
                                fill="black"
                            />
                        ))}
                    </mask>
                </defs>
                <rect
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    fill="rgba(0,0,0,0.5)"
                    mask="url(#ai-help-spotlight-mask)"
                />
            </svg>

            {/* Clickable cutout zones with pulsing click icon */}
            {rects.map(r => (
                <div
                    key={`cutout-${r.helpId}`}
                    style={{
                        position: 'absolute',
                        top: r.top - 4,
                        left: r.left - 4,
                        width: r.width + 8,
                        height: r.height + 8,
                        borderRadius: 8,
                        cursor: 'pointer',
                        zIndex: 1402,
                    }}
                    onClick={() => handleCutoutClick(r.helpId)}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            pointerEvents: 'none',
                            animation: 'ai-help-pulse 1.5s ease-in-out infinite',
                            zIndex: 1403,
                        }}
                    >
                        <MousePointerClick size={36} color="#22c55e" />
                    </div>
                </div>
            ))}
        </View>
    )
}
